import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useApp } from "../store";
import StructurePanel from "./StructurePanel";
import PreviewPane from "./PreviewPane";
import DesignPanel from "./DesignPanel";
import MarkdownEditor, { MarkerCheatsheet } from "./MarkdownEditor";
import { exportSettingsAsFile, importSettingsFromFile } from "../lib/persistence";
import { buildEpub, downloadBlob } from "../lib/exportEpub";
import { parseMarkdown } from "../lib/parseMarkdown";
import { injectBookmarks } from "../lib/pdfBookmarkInjector";
export default function EditorScreen() {
    const { settings, setSettings, filename, rawMarkdown, bodyHtml, headings, wordCount, images, imageOverrides, addImageOverrides, setRawMarkdown, setParsed, } = useApp();
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
    const [pdf2x4Modal, setPdf2x4Modal] = useState(false);
    // Bookmark-Export hat ein zweistufiges UI: erst Bookmarks abgreifen + drucken,
    // dann die vom User gespeicherte PDF entgegennehmen und Outline injizieren.
    const [bookmarkFlow, setBookmarkFlow] = useState(null);
    // Sidebar-Collapsed-State (persistiert in localStorage)
    const [leftCollapsed, setLeftCollapsed] = useState(() => localStorage.getItem("md2bp:left-collapsed") === "1");
    const [rightCollapsed, setRightCollapsed] = useState(() => localStorage.getItem("md2bp:right-collapsed") === "1");
    const [leftTab, setLeftTab] = useState(() => localStorage.getItem("md2bp:left-tab") ?? "struct");
    function selectLeftTab(t) {
        setLeftTab(t);
        localStorage.setItem("md2bp:left-tab", t);
    }
    // Reparse-Effekt: jede Änderung an rawMarkdown wird (debounced) neu geparst,
    // damit Preview/Outline up-to-date bleibt. Debounce 350ms — schneller als der
    // Preview-Render-Debounce (400ms), damit der Reparse noch vor dem Render
    // ankommt.
    useEffect(() => {
        if (!rawMarkdown)
            return;
        const handle = setTimeout(async () => {
            try {
                // skipNormalize: rawMarkdown ist seit dem Upload bereits normalisiert
                // → Offsets im neuen DOM matchen 1:1 den Store-State, und die Marker
                // werden im Click-to-Edit-Flow exakt an der richtigen Stelle eingefügt.
                const parsed = await parseMarkdown(rawMarkdown, {
                    smartQuotes: settings.smartQuotes,
                    language: settings.meta.language,
                    skipNormalize: true,
                });
                setParsed({
                    bodyHtml: parsed.html,
                    headings: parsed.headings,
                    images: parsed.images,
                    wordCount: parsed.wordCount,
                });
            }
            catch (e) {
                console.error("[md2bp] Reparse fehlgeschlagen:", e);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [rawMarkdown, settings.smartQuotes, settings.meta.language, setParsed]);
    function toggleLeft() {
        const next = !leftCollapsed;
        setLeftCollapsed(next);
        localStorage.setItem("md2bp:left-collapsed", next ? "1" : "0");
    }
    function toggleRight() {
        const next = !rightCollapsed;
        setRightCollapsed(next);
        localStorage.setItem("md2bp:right-collapsed", next ? "1" : "0");
    }
    function onExportSettings() {
        exportSettingsAsFile(settings, baseName(filename) + ".settings.json");
    }
    async function onImportSettings(e) {
        const file = e.target.files?.[0];
        if (!file)
            return;
        try {
            const s = await importSettingsFromFile(file);
            setSettings(s);
        }
        catch (err) {
            alert("JSON konnte nicht geladen werden: " + String(err.message));
        }
        finally {
            e.target.value = "";
        }
    }
    async function onExportEpub() {
        setBusy("EPUB wird gebaut…");
        try {
            const blob = await buildEpub({ settings, bodyHtml, headings });
            downloadBlob(blob, baseName(filename) + ".epub");
        }
        catch (e) {
            alert("EPUB-Export fehlgeschlagen: " + String(e.message));
        }
        finally {
            setBusy(null);
            setExportMenuOpen(false);
        }
    }
    function onExportPdf() {
        // PreviewPane stellt globale Funktion bereit, weil Iframe-Ref dort lebt.
        window.__md2bp_print?.();
        setExportMenuOpen(false);
    }
    function onExportMarkdown() {
        // Aktuelles Markdown mit allen Änderungen herunterladen.
        const blob = new Blob([rawMarkdown], { type: "text/markdown;charset=utf-8" });
        downloadBlob(blob, baseName(filename) + ".md");
        setExportMenuOpen(false);
    }
    function onOpenPdf2x4() {
        setExportMenuOpen(false);
        // PDF/X-4 entsteht auf dem eigenen Dienst pdf2x4 (im Browser, ohne Upload).
        // Hier nur das Druck-PDF erzeugen; das Popup erklärt den Weg und bietet die
        // Einstellungs-JSON für pdf2x4 an.
        setPdf2x4Modal(true);
    }
    // Fokus-JSON für pdf2x4 (Titel/Autor/Seitenmaße/Profil/Intent) — damit man die
    // Einstellungen dort nicht von Hand eingeben muss.
    function downloadPdf2x4Settings() {
        const payload = { meta: settings.meta, page: settings.page, print: settings.print };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        downloadBlob(blob, baseName(filename) + ".pdf2x4.json");
    }
    function onExportPdfWithBookmarks() {
        const entries = window.__md2bp_extract_bookmarks?.() ?? [];
        if (entries.length === 0) {
            alert("Keine Headings für Lesezeichen gefunden. Wurde die Preview schon gerendert?");
            return;
        }
        setBookmarkFlow({ entries });
        setExportMenuOpen(false);
        // Direkt den Druck-Dialog öffnen — der User speichert PDF, dann lädt er sie unten hoch.
        setTimeout(() => window.__md2bp_print?.(), 100);
    }
    // ----- Fehlende Bilder ----------------------------------------------------
    // Ein Bild gilt als „aufgelöst" wenn der src bereits eine Daten-/HTTP-URL ist
    // ODER die Overrides-Map einen Eintrag (per exaktem src oder Basename) hat.
    function isResolved(src) {
        if (!src)
            return false;
        if (src.startsWith("data:") || /^https?:\/\//.test(src))
            return true;
        if (imageOverrides[src])
            return true;
        const base = (src.split(/[\\/]/).pop() || src).toLowerCase();
        for (const k of Object.keys(imageOverrides)) {
            const kb = (k.split(/[\\/]/).pop() || k).toLowerCase();
            if (kb === base)
                return true;
        }
        return false;
    }
    const missingImages = images.filter((i) => !isResolved(i.src));
    const missingBasenames = new Set(missingImages.map((i) => (i.src.split(/[\\/]/).pop() || i.src).toLowerCase()));
    async function fileToDataUrl(file) {
        return new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result));
            r.onerror = () => reject(r.error);
            r.readAsDataURL(file);
        });
    }
    async function onImagesPicked(e) {
        const files = Array.from(e.target.files ?? []);
        if (files.length === 0)
            return;
        setBusy(`Lade ${files.length} Bild${files.length === 1 ? "" : "er"}…`);
        try {
            const added = {};
            const skipped = [];
            for (const f of files) {
                const base = f.name.toLowerCase();
                if (!missingBasenames.has(base)) {
                    // Wir akzeptieren das Bild trotzdem unter seinem Basename — es matched
                    // dann eventuell beim nächsten Markdown-Load. Aber wir warnen den User.
                    skipped.push(f.name);
                }
                added[f.name] = await fileToDataUrl(f);
            }
            addImageOverrides(added);
            if (skipped.length > 0) {
                // Nicht hart aborten — nur Info-Toast
                console.warn("Bilder ohne passende Referenz im Markdown:", skipped);
            }
        }
        catch (err) {
            alert("Bild-Upload fehlgeschlagen: " + String(err.message));
        }
        finally {
            setBusy(null);
            e.target.value = "";
        }
    }
    async function onBookmarkPdfChosen(e) {
        const file = e.target.files?.[0];
        if (!file || !bookmarkFlow)
            return;
        setBusy("Lesezeichen werden eingefügt…");
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            const out = await injectBookmarks(bytes, bookmarkFlow.entries);
            // out: Uint8Array<ArrayBufferLike> — Blob-Konstruktor erwartet konkret
            // ArrayBuffer (nicht SharedArrayBuffer). Wir geben die Backing-Bytes als
            // frischen, sicher typed ArrayBuffer-Slice rein.
            const blob = new Blob([out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength)], { type: "application/pdf" });
            downloadBlob(blob, bookFilename(settings.meta?.title || "", settings.meta?.author || "", filename) + ".pdf");
            setBookmarkFlow(null);
        }
        catch (err) {
            alert("Injektion fehlgeschlagen: " + String(err.message));
        }
        finally {
            setBusy(null);
            e.target.value = "";
        }
    }
    return (_jsxs("div", { className: "editor-root", children: [_jsxs("header", { className: "editor-header", children: [_jsx("span", { className: "title", children: "md2bookpdf" }), _jsxs("span", { className: "crumb", children: ["\u203A \u201E", filename, "\" \u00B7 ", wordCount.toLocaleString("de-DE"), " W\u00F6rter"] }), _jsx("span", { className: "grow" }), _jsx("button", { className: "btn ghost", onClick: onExportSettings, children: "JSON exportieren" }), _jsxs("label", { className: "btn ghost", children: ["JSON importieren", _jsx("input", { type: "file", accept: ".json", hidden: true, onChange: onImportSettings })] }), _jsxs("div", { className: "dropdown", children: [_jsx("button", { className: "btn primary", onClick: () => setExportMenuOpen((o) => !o), children: "Generieren \u25BE" }), exportMenuOpen && (_jsxs("div", { className: "dropdown-menu", onMouseLeave: () => setExportMenuOpen(false), children: [_jsx("button", { onClick: onExportMarkdown, children: "Aktuelle MD exportieren" }), _jsx("button", { onClick: onExportPdf, children: "PDF (Druck-Dialog)" }), _jsx("button", { onClick: onExportPdfWithBookmarks, children: "PDF mit Lesezeichen\u2026" }), _jsx("button", { onClick: onOpenPdf2x4, title: "Erst hier \u201EPDF (Druck-Dialog)\u201C speichern, dann auf pdf2x4.services.gutermann.gmbh zu PDF/X-4 umwandeln", children: "PDF/X-4 f\u00FCr die Druckerei \u2197" }), _jsx("button", { onClick: onExportEpub, children: "EPUB herunterladen" })] }))] })] }), missingImages.length > 0 && (_jsxs("div", { className: "missing-images-banner", children: [_jsx("span", { className: "mi-icon", children: "\uD83D\uDDBC" }), _jsxs("span", { className: "mi-text", children: [_jsxs("b", { children: [missingImages.length, " Bild", missingImages.length === 1 ? "" : "er", " fehlen"] }), " — ", missingImages.slice(0, 3).map((i) => i.src.split(/[\\/]/).pop()).join(", "), missingImages.length > 3 && ` +${missingImages.length - 3} weitere`] }), _jsxs("label", { className: "btn primary mi-btn", children: ["Bilder hochladen", _jsx("input", { type: "file", accept: "image/*", multiple: true, hidden: true, onChange: onImagesPicked })] })] })), _jsxs("div", { className: `editor-grid ${leftCollapsed ? "left-collapsed" : ""} ${rightCollapsed ? "right-collapsed" : ""}`, children: [_jsxs("aside", { className: "col col-struct", children: [_jsxs("div", { className: "col-head", children: [_jsxs("div", { className: "left-tab-row", children: [_jsx("button", { className: `left-tab ${leftTab === "struct" ? "active" : ""}`, onClick: () => selectLeftTab("struct"), children: "Struktur" }), _jsx("button", { className: `left-tab ${leftTab === "markdown" ? "active" : ""}`, onClick: () => selectLeftTab("markdown"), children: "Markdown" })] }), _jsx("button", { className: "collapse-btn", onClick: toggleLeft, title: "Einklappen", children: "\u25C0" })] }), leftTab === "struct" ? (_jsx(StructurePanel, {})) : (_jsx(MarkdownEditor, { value: rawMarkdown, onChange: setRawMarkdown, onOpenHelp: () => setCheatsheetOpen(true) }))] }), leftCollapsed && (_jsxs("button", { className: "collapse-rail collapse-rail-left", onClick: toggleLeft, title: "Struktur einblenden", children: [_jsx("span", { children: "Struktur" }), _jsx("span", { style: { fontSize: 14 }, children: "\u25B6" })] })), _jsx("section", { className: "col col-preview", children: _jsx(PreviewPane, {}) }), rightCollapsed && (_jsxs("button", { className: "collapse-rail collapse-rail-right", onClick: toggleRight, title: "Design einblenden", children: [_jsx("span", { style: { fontSize: 14 }, children: "\u25C0" }), _jsx("span", { children: "Design" })] })), _jsxs("aside", { className: "col col-design", children: [_jsxs("div", { className: "col-head", children: [_jsx("button", { className: "collapse-btn", onClick: toggleRight, title: "Einklappen", children: "\u25B6" }), _jsx("span", { children: "Design" })] }), _jsx(DesignPanel, {})] })] }), busy && _jsx("div", { className: "toast", children: busy }), cheatsheetOpen && _jsx(MarkerCheatsheet, { onClose: () => setCheatsheetOpen(false) }), pdf2x4Modal && (_jsx("div", { className: "modal-backdrop", onClick: () => setPdf2x4Modal(false), children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "PDF/X-4 f\u00FCr die Druckerei" }), _jsxs("p", { className: "bm-note", children: ["Das hier erzeugte Druck-PDF ist ", _jsx("b", { children: "noch nicht PDF/X-4-konform" }), ". Viele Druckereien verlangen aber ein zertifiziertes PDF/X-4 (CMYK-Farbprofil, eingebettete Schriften, korrekte Metadaten), damit deine Datei sicher korrekt verarbeitet wird."] }), _jsxs("ol", { className: "bm-steps", children: [_jsxs("li", { children: ["Oben im Men\u00FC ", _jsx("b", { children: "\u201EPDF (Druck-Dialog)\"" }), " w\u00E4hlen und als PDF speichern."] }), _jsxs("li", { children: ["Diese PDF auf ", _jsx("b", { children: "pdf2x4" }), " hochladen \u2014 die Umwandlung l\u00E4uft komplett im Browser, deine Datei wird zu keinem Server gesendet.", _jsxs("div", { style: { marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }, children: [_jsx("a", { className: "btn primary", href: "https://pdf2x4.services.gutermann.gmbh", target: "_blank", rel: "noopener", children: "pdf2x4 \u00F6ffnen \u2197" }), _jsx("button", { className: "btn ghost", onClick: downloadPdf2x4Settings, children: "Einstellungs-JSON f\u00FCr pdf2x4 herunterladen" })] })] }), _jsxs("li", { children: ["In pdf2x4 diese ", _jsx("b", { children: "Einstellungs-JSON" }), " laden \u2014 dann sind Titel, Autor, Seitenma\u00DFe und Farbprofil dort schon korrekt gesetzt."] })] }), _jsx("div", { className: "bm-actions", children: _jsx("button", { className: "btn ghost", onClick: () => setPdf2x4Modal(false), children: "Schlie\u00DFen" }) })] }) })), bookmarkFlow && (_jsx("div", { className: "modal-backdrop", onClick: () => setBookmarkFlow(null), children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "PDF mit Lesezeichen exportieren" }), _jsxs("ol", { className: "bm-steps", children: [_jsxs("li", { children: ["Im soeben ge\u00F6ffneten Druck-Dialog auf ", _jsx("b", { children: "\u201EAls PDF speichern\"" }), " klicken und die Datei auf deinem Rechner sichern. Falls der Dialog nicht erscheint:", " ", _jsx("button", { className: "btn ghost small", onClick: () => window.__md2bp_print?.(), children: "Druck-Dialog erneut \u00F6ffnen" })] }), _jsxs("li", { children: ["Soeben gespeicherte PDF unten ausw\u00E4hlen \u2014 die App f\u00FCgt", " ", bookmarkFlow.entries.length, " Lesezeichen ein und bietet die fertige Datei zum Download.", _jsx("div", { style: { marginTop: 10 }, children: _jsxs("label", { className: "btn primary", children: ["PDF ausw\u00E4hlen\u2026", _jsx("input", { type: "file", accept: "application/pdf,.pdf", hidden: true, onChange: onBookmarkPdfChosen })] }) })] })] }), _jsx("p", { className: "bm-note", children: "Die PDF verl\u00E4sst deinen Browser nicht \u2014 Verarbeitung l\u00E4uft lokal per pdf-lib." }), _jsx("div", { className: "bm-actions", children: _jsx("button", { className: "btn ghost", onClick: () => setBookmarkFlow(null), children: "Abbrechen" }) })] }) }))] }));
}
function baseName(filename) {
    return filename.replace(/\.(md|markdown|txt)$/i, "") || "buch";
}
/**
 * Dateiname aus Buch-Metadaten: "Titel – Autor". Fällt auf den Markdown-Namen
 * zurück, wenn keine Meta gesetzt ist. Sanitized für Dateisystem-Safety.
 */
function bookFilename(title, author, fallback) {
    const t = sanitizeForFs(title);
    const a = sanitizeForFs(author);
    if (t && a)
        return `${t} – ${a}`;
    if (t)
        return t;
    return baseName(fallback);
}
function sanitizeForFs(s) {
    // Verbotene Filename-Zeichen + Control-Chars raus, Whitespace normalisieren.
    return s.replace(/[<>:"/\\|?*\x00-\x1f]/g, "").replace(/\s+/g, " ").trim();
}
